import { authAuthority, onAuthSessionExpired, replaceAuthContext } from '@/@vxapp/Auth/authContext'
import { clearChatAuthority } from '@/@vxapp/Auth/chatAuthority'
import {
  LOGOUT_UNCONFIRMED,
  persistRevocationCredential,
  revokeLoginSession,
} from '@/@vxapp/Auth/sessionLogout'
import { defineStore } from 'pinia'
import { nextTick } from 'vue'
import { useApi } from '@/@vxapp/@Utils/apiClient'
import { Cookie } from '@/@vxapp/@Enums/Cookie'
import { Endpoint } from '@/@vxapp/@Enums/Endpoint'
import { createApplicationAbility } from '@/plugins/casl/abilityHelper'
import { useToast } from '@/plugins/toast/composables/useToast.js'
import { useCookie } from '@/@core/composable/useCookie.js'
import { useAuthHelpers } from '@/@vxapp/Auth/useAuthHelpers'
import { themeConfig } from '@themeConfig'
import { normalizeApiError, mapLoginErrorToMessage } from '@/@vxapp/@Utils/authError'
import { AuthError } from '@/@vxapp/@Enums/PHP/core/Enums/Errors/AuthError'
import { useNotificationsStore } from '@/@vxapp/useNotificationsStore'
import { PageMeta } from '@/@vxapp/@Enums/PageMeta'
import { AuthErrors } from '@/@vxapp/Auth/AuthErrors'
import {
  authLoginGeneration,
  beginAuthLogout,
  canSaveRevocationCredential,
  establishAuthLogin,
  isAuthLogoutPending,
  observeAuthLogout,
  readAuthLogoutIntent,
  notifyAuthTokenUpdated,
  waitForPeerAuthToken,
  withAuthRefreshLock,
} from '@/@vxapp/Auth/authRefreshCoordinator'
import { EnumUtils } from '@/@vxapp/@Utils/enumUtils'
import { StorageKey, StorageManager } from '@/@vxapp/@core/AppStorage/StorageManager'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import {
  isSessionEndReason,
  notifySessionEnded,
  sessionEndReasonForCode,
} from '@/@vxapp/Auth/sessionEnded'

const log = createLogger('AuthStore')
const renewalRequests = new WeakMap()
const sessionRenewalRequests = new WeakMap()
const logoutRequests = new WeakMap()
const logoutObservers = new WeakMap()
const sessionEndRequests = new WeakMap()
// Authority each store instance last adopted from storage.
const adoptedAuthorities = new WeakMap()
// Lifecycle versions produced by a guest credential failure resetting its own state.
const guestFailureResets = new WeakMap()

export const useAuthStore = defineStore('AuthStore', {
  state: () => ({
    whoCalledMe: null,
    isAuthenticated: false,
    isLoggingOut: false,
    logoutNotice: null,
    sessionEndNotice: /** @type {'expired' | 'invalid' | null} */ (null),
    isUser: false,
    isGuest: false,
    isStateSet: false,
    _initPromise: null,
    _authLifecycleVersion: 0,
    isTokenExpired: false,
    isTokenRenewAllowed: false,

    // for guest auth
    pageMeta: null,
    matchedPermission: null,

    // These values are all stored as cookies
    headers: null,
    token: null,
    api: null,
    role: null,
    userData: null,
    permissions: null,
    userPreferences: null,

    // CASL ability
    ability: /** @type {import('@casl/ability').AnyMongoAbility | null} */ (null),

    // status
    isLoading: false,
    jwtLastValidatedTS: null,
    bypassChecks: false,

    // error tracking (?) not sure what to call this
    // object of arrays of timestamps when ability has been blocked
    blockedAbilityTracker: null,
  }),
  actions: {
    ensureLogoutObserver() {
      if (logoutObservers.has(this)) return

      const stopExpiration = onAuthSessionExpired(async () => {
        this.resetState()
        try {
          const { router } = await import('@/router')

          await router.push({ name: PageMeta.Auth.login.name })
        } catch (error) {
          log.warn('Session expiration routing failed', error)
        }
      })

      const cleanup = observeAuthLogout(() => {
        const intent = readAuthLogoutIntent()
        if (!intent?.loggedOut) return
        replaceAuthContext()
        clearChatAuthority()
        this.isLoggingOut = true
        this.isAuthenticated = false
        this.permissions = null
        this.ability?.update([])
        if ((intent.finished || intent.deadline <= Date.now()) && !logoutRequests.has(this)) {
          this.resetState()
          this.logoutNotice = intent.serverConfirmed ? null : LOGOUT_UNCONFIRMED
        }
      })

      logoutObservers.set(this, cleanup)

      const dispose = this.$dispose.bind(this)

      this.$dispose = () => {
        cleanup()
        stopExpiration()
        logoutObservers.delete(this)
        dispose()
      }
    },
    async init(whoCalledMe, ignorePromise = false, expectedAuthLifecycleVersion = null) {
      this.ensureLogoutObserver()
      if (isAuthLogoutPending()) return
      expectedAuthLifecycleVersion ??= this._authLifecycleVersion
      if (ignorePromise) {
        if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
          return
        }

        this.setStateFromStorage()
        await this.setAbility()

        if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
          return
        }

        this.validateAuthStatus()
        this.saveAuthStatusStateToStorage(ignorePromise)

        return
      }

      // if someone already kicked off init, re-use it
      if (this._initPromise) {
        log.debug(
          `Init Call By ${whoCalledMe} Rejected, init already underway by ${this.whoCalledMe}`
        )

        return this._initPromise
      }

      this._initPromise = (async () => {
        try {
          this.whoCalledMe = whoCalledMe
          log.debug(`=============== Initializing Auth Store (${whoCalledMe}) ===============`)
          this.setStateFromStorage()
          await this.setAbility()
          this.validateAuthStatus()
          this.saveAuthStatusStateToStorage()
          log.debug('Initialization complete', this.$state)
        } finally {
          this._initPromise = null
        }
      })()

      return this._initPromise
    },

    // Getters
    getAbility(caller = '') {
      log.debug(`getAbility request by ${caller}`, this.ability)

      return this.ability
    },
    getPermissions(caller = '') {
      log.debug(`getPermissions request by ${caller}`, this.permissions)

      return this.permissions
    },
    getHeaders() {
      log.debug('getHeaders', this.headers)

      return this.headers
    },
    getUserData() {
      log.debug('Fetching User Data', this.userData)

      return this.userData
    },
    getUserPreferences() {
      log.debug('Fetching User Preferences', this.userPreferences)
      if (this.userPreferences) {
        return this.userPreferences
      }

      this.userPreferences = useCookie(Cookie.UserPreferences).value
      if (this.userPreferences) {
        return this.userPreferences
      }

      this.userPreferences = themeConfig.app.userPreferences
      log.warn('User Preferences Not Found -> Using Fallback', this.userPreferences)

      return this.userPreferences
    },
    getAuthStatus(validate = true) {
      if (validate) {
        this.validateAuthStatus()
        this.saveAuthStatusStateToStorage()
      }

      return {
        isAuthenticated: this.isAuthenticated,
        isUser: this.isUser,
        isGuest: this.isGuest,
        isTokenExpired: this.isTokenExpired,
        isTokenRenewAllowed: this.isTokenRenewAllowed,
        hasUserData: this.userData ? true : false,
        hasHeaders: this.headers ? true : false,
        isAuthHeaderSet: useAuthHelpers().getAuthHeader(this.headers) ? true : false,
        isApiHeaderSet: useAuthHelpers().getApiHeader(this.headers) ? true : false,
      }
    },
    isAuthLifecycleCurrent(expectedAuthLifecycleVersion) {
      if (this.isLoggingOut || isAuthLogoutPending()) return false

      return (
        expectedAuthLifecycleVersion === null ||
        expectedAuthLifecycleVersion === this._authLifecycleVersion
      )
    },

    // Setters
    setPageMeta(to, whoCalledMe, force = false) {
      log.debug(`setPageMeta called by ${whoCalledMe}`)

      const helpers = useAuthHelpers()
      const cookieRef = useCookie(Cookie.PageMeta)
      if (!force && !helpers.isCookieOld(cookieRef, 5)) {
        this.pageMeta = cookieRef.value.value
        log.debug('Page meta is still fresh, skipping overwrite', this.pageMeta)

        return this.pageMeta
      }

      this.pageMeta = to
      cookieRef.value = { value: to, timestamp: Date.now() }
      log.debug('Page meta has been saved', to)

      return to
    },
    setMatchedPermission(perm, force = false) {
      const helpers = useAuthHelpers()
      const cookieRef = useCookie(Cookie.MatchedPermission)
      if (!force && !helpers.isCookieOld(cookieRef, 5)) {
        log.debug('Matched Permission is still fresh, skipping overwrite')
        this.matchedPermission = cookieRef.value.value

        return this.matchedPermission
      }

      this.matchedPermission = perm
      cookieRef.value = { value: perm, timestamp: Date.now() }
      log.debug('Matched Permission has been saved', perm)

      return perm
    },
    setBypassChecks(val, whoCalledMe, durationMs = 5000) {
      this.bypassChecks = val
      if (val) {
        log.debug(`setBypassChecks ${val} -> Called by: ${whoCalledMe} -> Duration: ${durationMs}`)
        setTimeout(() => {
          log.debug('setBypassChecks FALSE -> checks re-enabled automatically')
          this.bypassChecks = false
        }, durationMs)
      } else {
        log.debug(`setBypassChecks FALSE -> checks enabled again -> Called by: ${whoCalledMe}`)
        this.bypassChecks = false
      }
    },
    readSessionEndNotice() {
      const notice = useCookie(Cookie.SessionEndNotice).value

      return isSessionEndReason(notice) ? notice : null
    },
    setSessionEndNotice(reason) {
      this.sessionEndNotice = reason
      useCookie(Cookie.SessionEndNotice).value = reason
    },
    clearSessionEndNotice() {
      this.sessionEndNotice = null
      useCookie(Cookie.SessionEndNotice).value = null
    },
    resetPageMeta() {
      this.pageMeta = null
      this.matchedPermission = null
      useCookie(Cookie.PageMeta).value = null
      useCookie(Cookie.MatchedPermission).value = null
    },

    /**
     * Tracks and limits how often a user is blocked by ability checks.
     * MINUTE_THRESHHOLD: time window in ms (e.g. 60000 for 1 min)
     * Returns the count of recent blocks.
     */
    abilityBlockHandler(MINUTE_THRESHHOLD = 60000) {
      if (!this.blockedAbilityTracker) {
        this.blockedAbilityTracker = []
      }

      const now = Date.now()

      // Add the current timestamp to the tracker
      this.blockedAbilityTracker.push(now)

      // Remove entries older than MINUTE_THRESHHOLD
      this.blockedAbilityTracker = this.blockedAbilityTracker.filter(
        ts => now - ts <= MINUTE_THRESHHOLD
      )

      const entriesFound = this.blockedAbilityTracker.length

      return {
        count: entriesFound,
        windowMs: MINUTE_THRESHHOLD,
        recentBlocks: this.blockedAbilityTracker.slice(), // for debugging
      }
    },

    // Auth Methods
    validateAuthStatus() {
      log.debug('validateAuthStatus: Setting Authentication Status')
      let error = null
      if (!this.headers) {
        error = 'No headers'
      }

      if (!this.role && !error) {
        error = 'No Role'
      }

      if (!this.token && !error) {
        error = 'No token'
      }

      if (error) {
        log.debug(`validateAuthStatus: ${error}`)
        this.isAuthenticated = false
        this.isUser = false
        this.isGuest = false
        this.isTokenExpired = false
        this.isTokenRenewAllowed = false

        return
      }

      this.isAuthenticated = true

      this.validateJwtToken()
    },
    validateJwtToken(bypassChecksParam = false) {
      if (this.bypassChecks) {
        bypassChecksParam = true
      }
      let firstLoad = false

      // Set TS if it is not set
      if (!this.jwtLastValidatedTS) {
        this.jwtLastValidatedTS = Date.now()
        firstLoad = true
        log.debug('validateJwtToken: first load -> jwtLastValidatedTS set')
      }

      // Check if JWT has been validated in the last 1min
      const TEN_SECONDS = 10000
      const areValuesOld = Date.now() - this.jwtLastValidatedTS >= TEN_SECONDS
      if (!firstLoad && !areValuesOld && !bypassChecksParam) {
        log.debug('validateJwtToken: JWT validated recently already -> returning')

        return
      }

      if (!this.isAuthenticated) {
        log.debug('validateJwtToken: Nothing to do')

        return
      }

      log.debug('validateJwtToken: Determining Request Type')

      const helpers = useAuthHelpers()

      this.isUser = this.userData ? true : false
      this.isGuest = this.token && !this.isUser

      if (!this.isUser && !this.isGuest) {
        // this should theoretically never happen
      }

      log.debug('validateJwtToken: Request Booleans Set')

      this.isTokenExpired = helpers.isTokenExpired(this.token)
      this.isTokenRenewAllowed = helpers.isRenewalAllowed(this.token)
    },
    async setAbility() {
      const expectedVersion = this._authLifecycleVersion
      if (!this.isAuthLifecycleCurrent(expectedVersion)) return
      const helpers = useAuthHelpers()

      log.debug('setAbility: Setting CASL Ability')

      if (!this.permissions) {
        log.debug('setAbility: Permissions Not Set -> Fetching Anon Permissions')

        const { success } = await this.fetchPermissions()

        if (!success) {
          log.warn('setAbility: could not fetch guest permissions, aborting ability setup')

          return
        } else {
          log.debug('setAbility: Permissions Fetched Successfully -> Creating Ability')
        }
      } else {
        log.debug('setAbility: Using Saved Permissions', this.permissions)
      }

      if (!this.permissions) {
        log.error('setAbility: Permissions is null -> setting to empty array')
        this.permissions = []
      }

      if (!this.isAuthLifecycleCurrent(expectedVersion)) return
      const incomingAbility = createApplicationAbility(this.permissions)
      const isValid = helpers.validateAbility(incomingAbility, this.ability)

      if (!isValid) {
        return
      }

      if (!this.ability) {
        this.ability = incomingAbility
        log.debug('setAbility: Created new ability', this.ability)
      } else {
        this.ability.update(incomingAbility.rules)
        log.debug('setAbility: Updated existing ability rules', incomingAbility.rules)
      }
    },
    saveAuthStatusStateToStorage(bypassChecksParam = false) {
      if (this.bypassChecks) {
        bypassChecksParam = true
      }

      const helpers = useAuthHelpers()

      // check if vals are even set
      const authState = this.getAuthStatus(false)
      if (helpers.allStatusesInactive(authState)) {
        log.debug('saveAuthStatusStateToStorage: State is empty -> not saving')

        return
      }

      const authStatusCookie = useCookie(Cookie.AuthStatus)
      if (!authStatusCookie) {
        useCookie(Cookie.AuthStatus).value = { value: authState, timestamp: Date.now() }
        log.debug('saveAuthStatusStateToStorage: Cookie Not Found -> Creating')

        return
      }

      // Bypass checks if the these mismatch
      if (this.isTokenExpired && !authStatusCookie?.value?.value?.isTokenExpired) {
        log.debug('saveAuthStatusStateToStorage: Bypassing Checks -> Token is now expired')
        bypassChecksParam = true
      }

      if (!bypassChecksParam) {
        // check cookie ts
        if (!helpers.isCookieOld(authStatusCookie, 5)) {
          log.debug('saveAuthStatusStateToStorage: State still fresh -> not saving')

          return
        }
      }

      authStatusCookie.value = { value: authState, timestamp: Date.now() }
      log.debug('saveAuthStatusStateToStorage: Saving State To Cookie', authStatusCookie.value)

      return
    },
    saveHeadersToStorage() {
      useCookie(Cookie.Headers).value = this.headers
      log.debug('Saving Headers To Cookie', this.headers)

      return
    },
    saveTokenToStorage() {
      useCookie(Cookie.Token).value = this.token
      log.debug('Saving Token To Cookie', this.token)

      return
    },
    saveApiToStorage() {
      useCookie(Cookie.Api).value = this.api
      log.debug('Saving API To Cookie', this.api)

      return
    },
    saveRoleToStorage() {
      useCookie(Cookie.Role).value = this.role
      log.debug('Saving Role To Cookie', this.role)

      return
    },
    saveUserDataToStorage() {
      useCookie(Cookie.UserData).value = this.userData
      log.debug('Saving User Data To Cookie', this.userData)

      return
    },
    saveUserPreferencesToStorage() {
      useCookie(Cookie.UserPreferences).value = this.userPreferences
      log.debug('Saving User Preferences To Cookie', this.userPreferences)

      return
    },
    saveStateToStorage() {
      log.debug('Setting Cookies', this.$state)

      this.saveHeadersToStorage()
      this.saveTokenToStorage()
      this.saveApiToStorage()
      this.saveRoleToStorage()
      this.saveUserDataToStorage()
      this.saveUserPreferencesToStorage()
      this.saveAuthStatusStateToStorage(true)
    },
    setStateFromStorage() {
      const helpers = useAuthHelpers()

      const stored = {
        headers: useCookie(Cookie.Headers).value,
        token: useCookie(Cookie.Token).value,
        api: useCookie(Cookie.Api).value,
        role: useCookie(Cookie.Role).value,
        userData: useCookie(Cookie.UserData).value,
        permissions: helpers.loadPermissionsFromStorage(),
      }

      const persistedAuthStatus = useCookie(Cookie.AuthStatus).value?.value

      const hadAuthenticatedIdentity =
        persistedAuthStatus?.isUser === true || persistedAuthStatus?.isGuest === true

      if (!stored.headers && !stored.token && stored.permissions) {
        if (hadAuthenticatedIdentity) {
          log.warn(
            'Session cookies expired but localStorage permissions remain -> clearing stale permissions'
          )
        } else {
          log.debug('Clearing permissions cached without session credentials')
        }
        try {
          localStorage.removeItem(Cookie.Permissions)
        } catch (err) {
          log.warn('localStorage remove failed', err)
        }
        stored.permissions = null
      }

      // Every navigation re-reads storage. Replace the authority, and so reset business state
      // and reject in-flight requests, only when it differs from the state in memory or from
      // the authority last adopted here. Paths that clear or replace memory and storage without
      // their own replacement still change the adopted authority.
      const storedAuthority = authAuthority(stored)

      if (
        storedAuthority !== authAuthority(this) ||
        storedAuthority !== adoptedAuthorities.get(this)
      ) {
        replaceAuthContext()
        Object.assign(this, stored)
      } else if (JSON.stringify(stored.userData) !== JSON.stringify(this.userData)) {
        // Same actor; refresh display fields without replacing the authority.
        this.userData = stored.userData
      }
      adoptedAuthorities.set(this, storedAuthority)

      this.userPreferences = useCookie(Cookie.UserPreferences).value
      this.pageMeta = useCookie(Cookie.PageMeta).value?.value
      this.matchedPermission = useCookie(Cookie.MatchedPermission).value?.value
      this.sessionEndNotice = this.readSessionEndNotice()

      this.setAuthStatusStateFromStorage()

      log.debug('setStateFromStorage: All State Values Set', this.$state)
    },
    setAuthStatusStateFromStorage() {
      // Grab the cookie ref and immediately pull its value
      const authStatusRef = useCookie(Cookie.AuthStatus).value
      const savedState = authStatusRef?.value ?? null
      if (!savedState) {
        log.debug('setAuthStatusStateFromStorage: No Values Set -> AuthStatus Cookie Empty')

        return
      } else {
        log.debug('setAuthStatusStateFromStorage: Saved Values', savedState)
      }

      const template = this.getAuthStatus(false)

      //console.debug(`[AuthStore][setAuthStatusStateFromStorage] template `, template)

      // For each key in our template, if the cookie has
      // that same key, overwrite the store’s boolean.
      Object.keys(template).forEach(flag => {
        if (flag in savedState) {
          //console.debug(`[AuthStore][setAuthStatusStateFromStorage] Set ${flag}`, savedState[flag])
          // e.g. this.isAuthenticated = savedState.isAuthenticated
          this[flag] = savedState[flag]
        }
      })

      log.debug(
        'setAuthStatusStateFromStorage: Values Set',
        JSON.stringify(this.getAuthStatus(false))
      )
    },

    // helpers
    async delay(ms) {
      await new Promise(resolve => setTimeout(resolve, ms))
    },
    safeResetAfterLoginFailure() {
      replaceAuthContext()
      // Clear only sensitive auth state; preserve user preferences and page meta
      useCookie(Cookie.Headers).value = null
      useCookie(Cookie.Token).value = null
      useCookie(Cookie.Api).value = null
      useCookie(Cookie.Role).value = null
      useCookie(Cookie.UserData).value = null
      useCookie(Cookie.AuthStatus).value = null
      useCookie(Cookie.MatchedPermission).value = null

      // Do not write literal "null" string to localStorage
      try {
        localStorage.removeItem(Cookie.Permissions)
      } catch (err) {
        log.warn('safeResetAfterLoginFailure: localStorage remove failed', err)
      }
      this.blockedAbilityTracker = null

      // keep preferences and page meta
      this.headers = null
      this.token = null
      this.api = null
      this.role = null
      this.userData = null
      this.permissions = null
      this.isAuthenticated = false
      this.isUser = false
      this.isGuest = false
      this.isTokenExpired = false
      this.isTokenRenewAllowed = false
    },
    resetGuestAuthForLobby() {
      useCookie(Cookie.Headers).value = null
      useCookie(Cookie.Token).value = null
      useCookie(Cookie.Api).value = null
      useCookie(Cookie.Role).value = null
      useCookie(Cookie.UserData).value = null
      useCookie(Cookie.AuthStatus).value = null

      try {
        localStorage.removeItem(Cookie.Permissions)
      } catch (err) {
        log.warn('resetGuestAuthForLobby: localStorage remove failed', err)
      }

      this.headers = null
      this.token = null
      this.api = null
      this.role = null
      this.userData = null
      this.permissions = null
      this.ability?.update([])
      this.isAuthenticated = false
      this.isUser = false
      this.isGuest = false
      this.isTokenExpired = false
      this.isTokenRenewAllowed = false
      this.jwtLastValidatedTS = null
      this.blockedAbilityTracker = null
      this.bypassChecks = false
    },
    async setEmptyAbilityAndRoute() {
      const helpers = useAuthHelpers()

      this.permissions = []
      helpers.savePermissionsToStorage(this.permissions)
      await this.setAbility()
      try {
        const { router } = await import('@/router')

        router.push({ name: PageMeta.Auth.notAuthorized.name })
      } catch (err) {
        log.warn('setEmptyAbilityAndRoute: routing failed', err)
      }
    },
    async safeLogoutOrGuestFallback() {
      const helpers = useAuthHelpers()
      const hdrs = this.headers

      // Clear sensitive cookies/state but preserve preferences and page meta
      useCookie(Cookie.Token).value = null
      useCookie(Cookie.Role).value = null
      useCookie(Cookie.UserData).value = null
      useCookie(Cookie.AuthStatus).value = null
      useCookie(Cookie.MatchedPermission).value = null
      try {
        localStorage.removeItem(Cookie.Permissions)
      } catch (err) {
        log.warn('safeLogoutOrGuestFallback: localStorage remove failed', err)
      }
      this.permissions = null
      this.ability?.update([])

      if (hdrs && helpers.isHeaderApiHeaderSet(hdrs)) {
        const guest = await this.generateGuestCredentials()
        if (guest?.success) {
          await this.fetchPermissions(true)

          return
        }
      }

      try {
        const { router } = await import('@/router')

        router.push({ name: PageMeta.Auth.login.name })
      } catch (err) {
        log.warn('safeLogoutOrGuestFallback: routing failed', err)
      }
    },
    async logout(router) {
      if (logoutRequests.has(this)) return logoutRequests.get(this)
      const supported = Boolean(this.token && this.isUser && !this.isGuest)
      const loginGeneration = authLoginGeneration()
      const intent = beginAuthLogout()

      this.isLoggingOut = true
      replaceAuthContext()
      clearChatAuthority()

      const expectedVersion = ++this._authLifecycleVersion

      const request = (async () => {
        const outcome = await revokeLoginSession(
          supported,
          () => useCookie(Cookie.Headers).value,
          intent
        )

        if (
          expectedVersion === this._authLifecycleVersion &&
          loginGeneration === authLoginGeneration()
        ) {
          this.resetState()
          this.logoutNotice = outcome.message
          if (outcome.message)
            useNotificationsStore().addSnackbarNotification({
              type: 'warning',
              message: outcome.message,
              timeout: -1,
            })
          if (router) await router.push({ name: PageMeta.Auth.login.name })
        }

        return outcome
      })()

      logoutRequests.set(this, request)
      try {
        return await request
      } finally {
        logoutRequests.delete(this)
      }
    },
    /**
     * Clears all auth state. `preserveReturnRoute` keeps the saved return route and the
     * session-ended notice for the sign-in page that follows an ended session.
     */
    resetState(options = {}) {
      const preserveReturnRoute = options?.preserveReturnRoute === true

      replaceAuthContext()
      clearChatAuthority()

      const nextAuthLifecycleVersion = this._authLifecycleVersion + 1
      const ability = this.ability

      ability?.update([])

      const pageMeta = preserveReturnRoute ? this.pageMeta : null

      log.warn('resetState: Values Cleared')
      useCookie(Cookie.Headers).value = null
      useCookie(Cookie.Token).value = null
      useCookie(Cookie.Api).value = null
      useCookie(Cookie.Role).value = null
      useCookie(Cookie.UserData).value = null
      useCookie(Cookie.UserPreferences).value = null
      if (!preserveReturnRoute) {
        useCookie(Cookie.PageMeta).value = null
        useCookie(Cookie.SessionEndNotice).value = null
      }
      useCookie(Cookie.MatchedPermission).value = null
      useCookie(Cookie.AuthStatus).value = null
      try {
        localStorage.removeItem(Cookie.Permissions)
      } catch (err) {
        log.warn('resetState: localStorage remove failed', err)
      }
      // A peer may still own renewal; preserve its lease and the persisted logout generation.
      for (const key of Object.values(StorageKey)) {
        if (key !== StorageKey.RefreshInProg && key !== StorageKey.LogoutIntent)
          StorageManager().remove(key)
      }
      this.blockedAbilityTracker = null
      this.$reset()
      this.ability = ability
      this.pageMeta = pageMeta
      this._authLifecycleVersion = nextAuthLifecycleVersion
    },

    /**
     * Ends a signed-in session that the API no longer accepts. Concurrent and later calls in
     * the same auth lifecycle share one reset, notice and handler call. Without a registered
     * handler (for example in Slave) the state is reset and persisted without navigating.
     *
     * @param {'expired' | 'invalid'} reason
     */
    endSession(reason) {
      if (!isSessionEndReason(reason)) return Promise.resolve()
      if (this.isLoggingOut || isAuthLogoutPending()) return Promise.resolve()

      const current = sessionEndRequests.get(this)
      if (current?.version === this._authLifecycleVersion) return current.promise
      if (this.isGuest) return this.recoverGuestSession()
      if (!this.token) return Promise.resolve()

      const userGUID = this.userData?.userGUID
      const owner = this.api && userGUID ? { apiGUID: this.api, userGUID } : null

      log.warn(`endSession: ${reason}`)
      this.resetState({ preserveReturnRoute: true })
      this.setSessionEndNotice(reason)

      // Register the single-flight entry before the handler runs.
      const promise = Promise.resolve()
        .then(() => notifySessionEnded(reason, { owner }))
        .catch(error => {
          log.warn('endSession: sign-in navigation failed', error)
        })

      sessionEndRequests.set(this, { version: this._authLifecycleVersion, promise })

      return promise
    },
    recoverGuestSession() {
      const expectedVersion = ++this._authLifecycleVersion
      const headers = this.headers

      replaceAuthContext()
      useCookie(Cookie.Token).value = null
      useCookie(Cookie.Role).value = null
      useCookie(Cookie.UserData).value = null
      useCookie(Cookie.AuthStatus).value = null
      try {
        localStorage.removeItem(Cookie.Permissions)
      } catch (error) {
        log.warn('recoverGuestSession: localStorage remove failed', error)
      }
      this.token = null
      this.role = null
      this.userData = null
      this.permissions = null
      this.ability?.update([])
      this.isAuthenticated = false
      this.isUser = false
      this.isGuest = false
      this.isTokenExpired = false
      this.isTokenRenewAllowed = false

      const promise = (async () => {
        const helpers = useAuthHelpers()
        if (!headers || !helpers.isHeaderApiHeaderSet(headers)) return false

        const guest = await this.generateGuestCredentials(expectedVersion)
        if (!guest?.success || !this.isAuthLifecycleCurrent(expectedVersion)) return false

        const permissions = await this.fetchPermissions(true, expectedVersion)

        return permissions.success && this.isAuthLifecycleCurrent(expectedVersion)
      })()

      sessionEndRequests.set(this, { version: expectedVersion, promise })

      return promise
    },
    processAuthResponse(data) {
      this.ensureLogoutObserver()
      replaceAuthContext()

      const helpers = useAuthHelpers()

      this.headers = data?.serviceData?.headers
      this.token = helpers.getAuthHeader(this.headers)
      this.api = helpers.getApiHeader(this.headers)
      this.role = helpers.getRoleGUID(this.headers)
      this.userData = helpers.getUserData(this.headers)
      this.userPreferences = helpers.getUserPreferences()

      // Force auth-status recalculation for the newly issued token before persisting state.
      this.jwtLastValidatedTS = null
      this.validateAuthStatus()

      this.saveStateToStorage()
    },

    /**
     * Fetch the current user's permissions from the API using their roleGUID.
     * Saves to local storage on success
     * if this.role is null, anon permissions will be returned by api
     *
     * @param {boolean} [resetStatus]
     * @param {number | null} [expectedAuthLifecycleVersion] Lifecycle version the caller
     *   started under; a newer version cancels the fetch and discards its response.
     */
    async fetchPermissions(resetStatus = false, expectedAuthLifecycleVersion = null) {
      expectedAuthLifecycleVersion ??= this._authLifecycleVersion
      if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
        return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
      }

      this.isLoading = true

      const helpers = useAuthHelpers()
      const roleValue = this.role ?? null

      log.debug(`fetchPermissions: Fetching Permissions -> Role: ${roleValue}`)
      try {
        const { data, responseCode } = await useApi.apiCall(Endpoint.Auth.permissions, {
          guid: roleValue,
        })

        if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        }

        const perms = helpers.sanitizePermissions(data?.serviceData?.permissions)
        if (responseCode === 200 && Array.isArray(perms)) {
          log.debug('fetchPermissions: Permissions Fetched')
          helpers.savePermissionsToStorage(perms)
          this.permissions = perms

          //StorageManager().set(StorageKey.LastPermissionsRefresh, Date.now())

          if (resetStatus) {
            log.debug('fetchPermissions: Resetting Authentication Status')
            await this.init('fetchPermissions', true, expectedAuthLifecycleVersion)

            if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
              return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
            }
          }

          this.isLoading = false

          return { success: true, permissions: perms }
        }

        const nerr = normalizeApiError(data)

        log.warn('fetchPermissions: Non-200 response', nerr)
        switch (nerr.code) {
          case AuthError.NO_PERMISSIONS_FOUND_FOR_ROLE:
          case AuthError.NO_PERMISSION_DATA_SET:
          case AuthError.ROLE_NOT_FOUND:
          case AuthError.ROLE_DOES_NOT_EXIST:
            await this.setEmptyAbilityAndRoute()
            this.isLoading = false

            return { success: false, error: nerr }
          default:
            this.isLoading = false

            return { success: false, error: nerr }
        }
      } catch (err) {
        if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        }

        const nerr = normalizeApiError(err)

        log.error('fetchPermissions: error', nerr)
        switch (nerr.code) {
          case AuthError.NO_PERMISSIONS_FOUND_FOR_ROLE:
          case AuthError.NO_PERMISSION_DATA_SET:
          case AuthError.ROLE_NOT_FOUND:
          case AuthError.ROLE_DOES_NOT_EXIST:
            await this.setEmptyAbilityAndRoute()
            this.isLoading = false

            return { success: false, error: nerr }
          default:
            this.isLoading = false

            return { success: false, error: nerr }
        }
      }
    },

    /**
     * Login
     */
    async login(email, password, rememberMe) {
      const expectedVersion = this._authLifecycleVersion

      this.isLoading = true
      void rememberMe

      const notifications = useNotificationsStore()

      try {
        const { data, responseCode } = await useApi.apiCall(Endpoint.Auth.login, {
          authID: email,
          authPassword: password,
        })

        if (responseCode === 201 && data?.serviceData?.headers) {
          if (expectedVersion !== this._authLifecycleVersion)
            return {
              success: false,
              error: normalizeApiError({ message: 'This sign-in was superseded by logout.' }),
            }
          establishAuthLogin()
          this._authLifecycleVersion += 1
          this.isLoggingOut = false
          this.logoutNotice = null
          this.clearSessionEndNotice()
          this.processAuthResponse(data)
          this.isLoading = false

          return { success: true }
        }
        const nerr = normalizeApiError(data)
        const uiMsg = mapLoginErrorToMessage(nerr.code)

        const isCredIssue = [
          AuthError.USER_NOT_FOUND,
          AuthError.USER_INVALID_PASSWORD,
          AuthError.USER_NO_PASSWORD,
          AuthError.USER_LOGIN_DISABLED,
        ].includes(nerr.code)

        const loginLogMethod = isCredIssue ? 'warn' : 'error'

        log[loginLogMethod]('login failed', { code: nerr.code, httpStatus: nerr.httpStatus })
        this.safeResetAfterLoginFailure()
        notifications[isCredIssue ? 'warn' : 'error'](uiMsg)
        this.isLoading = false

        return { success: false, error: nerr }
      } catch (err) {
        const nerr = normalizeApiError(err)

        this.safeResetAfterLoginFailure()

        const uiMsg = mapLoginErrorToMessage(nerr.code)

        log.error('login error', { code: nerr.code, httpStatus: nerr.httpStatus })

        const notifications = useNotificationsStore()

        notifications.error(uiMsg)
        this.isLoading = false

        return { success: false, error: nerr }
      }
    },

    /**
     * Starts a guest authentication attempt and returns its lifecycle version. Beginning an
     * attempt supersedes every earlier guest attempt, token renewal and guest recovery that is
     * still in flight. While a logout is pending the version is left unchanged, so the
     * returned attempt is already stale and cannot restore a guest session.
     *
     * @returns {number}
     */
    beginGuestAuthentication() {
      if (this.isLoggingOut || isAuthLogoutPending()) return this._authLifecycleVersion

      return ++this._authLifecycleVersion
    },

    /**
     * Supersedes an unfinished guest attempt, for example when its page unmounts. Only the
     * current attempt advances the version, and a pending logout is never disturbed.
     *
     * @param {number} attempt Version returned by `beginGuestAuthentication`.
     * @returns {boolean} Whether the lifecycle version advanced.
     */
    supersedeGuestAuthentication(attempt) {
      if (!this.isAuthLifecycleCurrent(attempt)) return false

      this._authLifecycleVersion += 1

      return true
    },

    /**
     * Whether something newer than this guest attempt has taken over the auth lifecycle: a
     * later attempt, recovery or login, or a pending logout. The attempt's own failed
     * credential request also resets state and advances the version; that does not count,
     * so a failing current attempt can still report its error.
     *
     * @param {number} attempt Version returned by `beginGuestAuthentication`.
     * @returns {boolean}
     */
    isGuestAttemptSuperseded(attempt) {
      if (this.isLoggingOut || isAuthLogoutPending()) return true
      if (attempt === this._authLifecycleVersion) return false

      const reset = guestFailureResets.get(this)

      return !(reset?.from === attempt && reset.to === this._authLifecycleVersion)
    },

    /**
     * Guest Credentials (required apiGUID header / URL)
     *
     * Writes nothing and calls no API when the attempt is already superseded, and does not
     * touch the session when a later attempt supersedes this one while its request is pending.
     *
     * @param {Record<string, string>} hdrs Canary response headers carrying the API GUID.
     * @param {number | null} [expectedVersion] Attempt from `beginGuestAuthentication`; a new
     *   attempt is begun when omitted.
     * @returns {Promise<boolean>} Whether this attempt is current and obtained credentials.
     */
    async processGuestAuthentication(hdrs, expectedVersion = null) {
      log.debug('Processing Guest Authentication -> Validating Response Headers')

      const attempt = expectedVersion ?? this.beginGuestAuthentication()

      if (!this.isAuthLifecycleCurrent(attempt)) {
        log.debug('processGuestAuthentication: Attempt superseded')

        return false
      }

      const helpers = useAuthHelpers()
      if (!helpers.isHeaderApiHeaderSet(hdrs)) {
        log.error('Response Headers Were Invalid')

        return false
      }

      this.headers = helpers.formatResponseHeaders(hdrs)

      if (this.headers) {
        log.debug('processGuestAuthentication: Saving Headers Cookie')
        this.saveHeadersToStorage()
      } else {
        log.error('processGuestAuthentication: Canary Response Headers Were Invalid')

        return false
      }

      log.debug('Processing Guest Authentication -> Making Guest Credentials Request')

      const { success } = await this.generateGuestCredentials(attempt)

      return success && this.isAuthLifecycleCurrent(attempt)
    },
    async generateGuestCredentials(expectedVersion = this._authLifecycleVersion) {
      try {
        const { data, responseCode } = await useApi.apiCall(Endpoint.Auth.guests)

        if (!this.isAuthLifecycleCurrent(expectedVersion))
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        if (responseCode === 201 && data?.serviceData?.headers) {
          this.processAuthResponse(data)
          log.debug('Processed Guest Authentication -> Token Granted')

          return { success: true }
        }

        log.error('Guest token failed: unexpected response format')
        this.resetGuestAfterFailure(expectedVersion)

        return { success: false }
      } catch (err) {
        if (!this.isAuthLifecycleCurrent(expectedVersion))
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        log.error('Guest Credentials error', err)
        this.resetGuestAfterFailure(expectedVersion)

        return { success: false, error: err }
      }
    },
    /**
     * Resets state after the current guest credential request failed, and records the version
     * that reset produced so `isGuestAttemptSuperseded` can tell it from a newer takeover.
     *
     * @param {number} expectedVersion Version the failed request ran under.
     */
    resetGuestAfterFailure(expectedVersion) {
      this.resetState()
      guestFailureResets.set(this, { from: expectedVersion, to: this._authLifecycleVersion })
    },

    /**
     * Refresh Expired JWT
     */
    async refreshToken(
      expectedAuthLifecycleVersion = this._authLifecycleVersion,
      lockHeld = false
    ) {
      if (this.isLoggingOut || isAuthLogoutPending())
        return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
      if (renewalRequests.has(this)) return renewalRequests.get(this)

      const request = lockHeld
        ? this.performTokenRefresh(expectedAuthLifecycleVersion)
        : withAuthRefreshLock(() => this.performTokenRefresh(expectedAuthLifecycleVersion)).then(
            result =>
              result.acquired
                ? result.result
                : { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
          )

      renewalRequests.set(this, request)
      try {
        return await request
      } finally {
        renewalRequests.delete(this)
      }
    },
    async performTokenRefresh(expectedAuthLifecycleVersion) {
      if (isAuthLogoutPending() || this.isLoggingOut)
        return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
      const loginGeneration = authLoginGeneration()
      try {
        const { data, responseCode } = await useApi.apiCall(
          Endpoint.Auth.refresh,
          {},
          { timeout: 5000 }
        )

        if (isAuthLogoutPending() || expectedAuthLifecycleVersion !== this._authLifecycleVersion) {
          if (
            responseCode === 200 &&
            data?.serviceData?.headers &&
            canSaveRevocationCredential(loginGeneration)
          )
            persistRevocationCredential(data.serviceData.headers)

          await nextTick()

          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        }

        if (responseCode === 200 && data?.serviceData?.headers) {
          this.processAuthResponse(data)
          await nextTick()
          notifyAuthTokenUpdated()

          return { success: true }
        }

        return { success: false, error: data }
      } catch (err) {
        if (expectedAuthLifecycleVersion !== this._authLifecycleVersion) {
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
        }

        return { success: false, error: err }
      }
    },

    async adoptPeerRenewedToken(staleToken, expectedAuthLifecycleVersion) {
      const peerToken = await waitForPeerAuthToken(staleToken, () => useCookie(Cookie.Token).value)

      if (!peerToken || !this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
        return false
      }

      await this.init('Auth Store (peer token recovery)', true, expectedAuthLifecycleVersion)

      if (!this.isAuthLifecycleCurrent(expectedAuthLifecycleVersion)) {
        return false
      }

      const authStatus = this.getAuthStatus()

      const peerTokenAdopted =
        this.token === peerToken && authStatus.isAuthenticated && !authStatus.isTokenExpired

      if (peerTokenAdopted) {
        log.debug('handleExpiredToken: Adopted token renewed by another tab')
      } else {
        log.warn('handleExpiredToken: Peer token was not a valid authenticated session')
      }

      return peerTokenAdopted
    },

    async ensureSession(minValiditySeconds = 0) {
      const version = this._authLifecycleVersion
      if (!this.isAuthLifecycleCurrent(version))
        return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }

      const persistedToken = useCookie(Cookie.Token).value
      if (this.token && persistedToken && persistedToken !== this.token) {
        await this.init('Request credential synchronization', true, version)
        if (!this.isAuthLifecycleCurrent(version))
          return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
      }
      if (!this.token) return { success: true }
      const helpers = useAuthHelpers()
      const expiresAt = helpers.getTokenExpiry(this.token)
      if (expiresAt > Date.now() / 1000 + minValiditySeconds) return { success: true }
      if (!helpers.isRenewalAllowed(this.token))
        return { success: expiresAt > Date.now() / 1000, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }

      return this.handleExpiredToken()
    },

    async handleExpiredToken() {
      if (!this.isAuthLifecycleCurrent(this._authLifecycleVersion))
        return { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }
      if (sessionRenewalRequests.has(this)) return sessionRenewalRequests.get(this)
      const request = this.performSessionRenewal()

      sessionRenewalRequests.set(this, request)
      try {
        return await request
      } finally {
        sessionRenewalRequests.delete(this)
      }
    },

    async performSessionRenewal() {
      const version = this._authLifecycleVersion
      const staleToken = this.token
      const cancelled = { success: false, error: AuthErrors.TOKEN_RENEWAL_CANCELLED }

      const locked = await withAuthRefreshLock(async () => {
        if (!this.isAuthLifecycleCurrent(version)) return cancelled
        const persistedToken = useCookie(Cookie.Token).value
        if (
          persistedToken &&
          persistedToken !== staleToken &&
          (await this.adoptPeerRenewedToken(staleToken, version))
        )
          return { success: true }

        return this.refreshToken(version, true)
      })

      if (!this.isAuthLifecycleCurrent(version)) return cancelled
      if (!locked.acquired) {
        if (await this.adoptPeerRenewedToken(staleToken, version))
          return { success: true, error: null }

        return { success: false, error: AuthErrors.TOKEN_ALREADY_REFRESHING }
      }
      const { success, error } = locked.result
      if (!success) {
        if (error === AuthErrors.TOKEN_RENEWAL_CANCELLED) return cancelled
        const code = EnumUtils().tryFrom(AuthError, error?.error)
        if (
          code === AuthError.NO_SESSION_FOUND_INVALID_TOKEN &&
          (await this.adoptPeerRenewedToken(staleToken, version))
        )
          return { success: true, error: null }
        if (!this.isAuthLifecycleCurrent(version)) return cancelled
        const reason = sessionEndReasonForCode(code)
        if (reason && this.isGuest) {
          const success = await this.endSession(reason)

          return { success, error: code, guestRecovered: success }
        }
        if (code) this.authErrorHandler(code)

        return { success: false, error: code ?? error }
      }

      // Credentials are persisted and the renewal lock is released before permissions:
      // logout must be able to revoke even if this independent read is slow.
      const permissions = await this.fetchPermissions(true, version)
      if (!this.isAuthLifecycleCurrent(version)) return cancelled

      return permissions.success
        ? { success: true, error: null }
        : { success: false, error: permissions.error }
    },

    // =============================
    authErrorHandler(error) {
      const sessionEndReason = sessionEndReasonForCode(error)
      if (sessionEndReason) {
        log.warn('authErrorHandler: ' + error)
        // Guests keep the existing reset without sign-in navigation.
        if (this.isGuest) this.resetState()
        else void this.endSession(sessionEndReason)

        return
      }

      const toast = useToast()

      log.error('authErrorHandler: ' + error)
      this.resetState()
      toast.error(error)
      toast.info('An error occured, sorry, please close the page and try again.')
      switch (error) {
        case AuthError.RENEW_TOKEN_FAILED:
          return
        case AuthError.SESSION_RENEWAL_FAILED:
          return
        case AuthError.USER_JWT_TOKEN_FAILED:
          /*if (!triedOnce) {
              console.error(LOG_PREFIX + error.code)
              
              triedOnce = true
              await this.delay(700)
              return await attemptRefresh()
            } else {
              await this.safeLogoutOrGuestFallback()
              return { success: false }
            }*/
          return
        case AuthError.AUTH_HEADER_REQUIRED:
          return
        case AuthError.AUTH_HEADER_ERROR:
          return
        case AuthError.TOKEN_NOT_SET:
          //await this.safeLogoutOrGuestFallback()
          return
        default:
          log.error('authErrorHandler: ' + (error?.code ?? 'Unknown error'))

          //await this.safeLogoutOrGuestFallback()
          return
      }
    },
  },
})
